import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData, ImageFileUpload } from "../../../utility/Utility"; // Adjust this import if needed
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

export default function AddHelpAndSupport() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: "",
    category: "",
    description: "",
    attachment: null,
    image: null,
    status: "active"
  });
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      setCategoriesLoading(true);
      try {
        const response = await postData("api/support/categories/list", {});
        if (response?.success) {
          // Removed unused categories state
        } else {
          toast.error(response?.message || "Failed to fetch categories");
        }
      } catch (error) {
        toast.error("Error fetching categories");
        console.error("Fetch categories error:", error);
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "attachment" || name === "image") {
      setFormData({ ...formData, [name]: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const toggleActive = () => {
    setFormData({ ...formData, status: formData.status === "active" ? "inactive" : "active" });
  };

  // Function to upload image
  const uploadImage = async () => {
    if (!formData.image) return null;
    const imageFormData = new FormData();
    imageFormData.append("file", formData.image);

    try {
      const response = await ImageFileUpload("/api/admin/upload?is_upload=s3", imageFormData);
      const data = await response;
      if (response?.success) {
        return data.url || data.imageUrl || data.filePath; // Adjust based on your API response
      } else {
        toast.error(data.message || "Image upload failed");
        return null;
      }
    } catch {
      toast.error("Network error during image upload");
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const uploadedImagePath = await uploadImage();
      if (formData.image && !uploadedImagePath) {
        setLoading(false);
        return;
      }

      const payload = {
        title: formData.title,
        category: formData.category,
        description: formData.description,
        attachment: formData.attachment || "",
        image: uploadedImagePath || "",
        status: formData.status
      };

      const response = await postData("api/support/tickets", payload);

      if (response?.success) {
        toast.success("Ticket created successfully");
        navigate("/help-and-support-list");
      } else {
        toast.error(response?.message || "Failed to create ticket");
      }
    } catch (error) {
      toast.error("Error creating ticket");
      console.error("Create ticket error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={5000} />
      <CustomLoader loading={loading || categoriesLoading} />
      <div className="content">
        <div className="container-fluid">
          {/* Header & Breadcrumbs */}
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            {/* ... */}
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              <i className="fas fa-arrow-left me-1"></i> Back
            </button>
          </div>

          {/* Form Card */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                {/* Card Header */}
                <div className="card-header">
                  <h5 className="card-title mb-0">Create New Help & Support</h5>
                </div>
                {/* Card Body */}
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    {/* Title */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="title" className="form-label fw-medium">
                          Title <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="text"
                          id="title"
                          name="title"
                          className="form-control"
                          placeholder="Enter title"
                          value={formData.title}
                          onChange={handleInputChange}
                          required
                          minLength={3}
                          maxLength={100}
                        />
                      </div>
                    </div>

                    {/* Category */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="category" className="form-label fw-medium">
                          Category <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <select
                          id="category"
                          name="category"
                          className="form-select"
                          value={formData.category}
                          onChange={handleInputChange}
                          required
                          disabled={categoriesLoading}
                        >
                          <option value="">Select a category</option>
                          {['policy_issue', 'claims', 'payment_problem', 'technical', 'general_query', 'account_related', 'feedback', 'other'].map((category) => (
                            <option key={category} value={category}>
                              {category.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                            </option>
                          ))}
                          {/* <option value="">Select a category</option>
                          {categories.map((category) => (
                            <option key={category} value={category}>
                              {category.charAt(0).toUpperCase() + category.slice(1)}
                            </option>
                          ))} */}
                        </select>
                        {categoriesLoading && (
                          <div className="mt-2 text-muted">Loading categories...</div>
                        )}
                      </div>
                    </div>

                    {/* Image Upload */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label
                          htmlFor="image"
                          className="form-label fw-medium"
                        >
                          Category Image <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="file"
                          id="image"
                          name="image"
                          className="form-control"
                          onChange={handleInputChange}
                          accept="image/*"
                          required
                        />
                        <small className="text-muted d-block mt-1">
                          Recommended: 800x600px, Max 2MB (JPEG/PNG)
                        </small>
                        {formData.image && (
                          <div className="mt-2">
                            <img
                              src={URL.createObjectURL(formData.image)}
                              alt="Preview"
                              className="img-thumbnail"
                              style={{ maxWidth: "200px", maxHeight: "150px" }}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status Switch */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label className="form-label fw-medium">Status</label>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check form-switch d-flex align-items-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="status"
                            checked={formData.status === "active"}
                            onChange={toggleActive}
                            role="switch"
                            style={{ width: "2.5em", height: "1.25em" }}
                          />
                          <label className="form-check-label ms-2" htmlFor="status">
                            {formData.status === "active" ? (
                              <span className="text-success">Open</span>
                            ) : (
                              <span className="text-muted">Closed</span>
                            )}
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <label htmlFor="description" className="form-label fw-medium">
                          Description <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <textarea
                          className="form-control"
                          id="description"
                          name="description"
                          rows={4}
                          placeholder="Describe the category..."
                          value={formData.description}
                          onChange={handleInputChange}
                          required
                          minLength={10}
                        />
                        <small className="text-muted">Minimum 10 characters</small>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="row mt-4">
                      <div className="col-md-6 offset-md-3">
                        <button
                          type="submit"
                          className="btn btn-primary py-2"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Processing...
                            </>
                          ) : (
                            "Submit"
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}